/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import {
  Button,
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Spinner,
} from "reactstrap";
import {
  DynamicFeed,
  ImageOutlined,
  Schedule,
  LiveHelp,
  AccountCircle,
} from "@material-ui/icons";
import SingleQuestionImage from "../components/SingleQuestionImage";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { useParams } from "react-router-dom";
import { useGetSingleImageIntell } from "../hooks/useGetSingleImageIntell";
import { useAskQuestionFromImageIntell } from "../hooks/useAskQuestionFromImageIntell";
import Toastr from "toastr";

const ImageIntellExecutionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [images, setImages] = useState<
    { original: string; thumbnail: string }[]
  >([]);
  const [questionText, setQuestionText] = useState<string>("");
  const [questions, setQuestions] = useState<
    {
      id: number;
      userId: number;
      question: string;
      executionId: number;
      response: {
        messages: {
          text: string;
          type: string;
        };
        cost: number;
      };
      updatedAt: string;
      createdAt: string;
    }[]
  >([]);

  const {
    mutateAsync: askQuestion,
    isError: isAskQuestionError,
    isLoading: isAskquestionLoading,
    data: askedQuestionData,
  } = useAskQuestionFromImageIntell();

  const {
    data: executionInfo,
    isLoading,
    isError,
    refetch,
  } = useGetSingleImageIntell(id);

  const askQuestionFromImages = async () => {
    askQuestion({ prompt: questionText, executionId: id });
  };

  useEffect(() => {
    if (executionInfo?.id) {
      let imgs: {
        original: string;
        thumbnail: string;
      }[] = [];

      for (let image of executionInfo.images) {
        imgs.push({
          original: image,
          thumbnail: image,
        });
      }
      setImages(imgs);

      setQuestions(executionInfo.questions.reverse());
    }
  }, [executionInfo]);

  useEffect(() => {
    if (askedQuestionData && askedQuestionData.id) {
      refetch();
    }
  }, [askedQuestionData, refetch]);

  useEffect(() => {
    if (isAskQuestionError) {
      Toastr.error("Something bad happened.");
    }
  }, [isAskQuestionError, refetch]);

  useEffect(() => {
    if (isError) {
      Toastr.error("Something bad happened.");
    }
  }, [isError]);

  useEffect(() => {
    refetch();
  }, []);

  return (
    <>
      <div
        className="header pb-8 pt-5 pt-lg-8 d-flex align-items-center"
        style={{
          height: "450px",
          backgroundColor: "#2D315C",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: "url(/background-header-infinity.jpg)",
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container className="align-items-center" fluid>
          <Row>
            <Col>
              <h2 className="display-3 text-white">
                Image Intell : Execution : {id}{" "}
                {isLoading && (
                  <>
                    <Spinner />
                  </>
                )}
              </h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card className="card-stats mb-4 mb-xl-0">
                <CardBody>
                  <Row>
                    <div className="col">
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0"
                      >
                        Executed at
                      </CardTitle>
                      <span className="h5 font-weight-bold mb-0">
                        {executionInfo && executionInfo?.createdAt && (
                          <span>
                            {DateTime.fromJSDate(
                              new Date(executionInfo.createdAt)
                            ).toRelative()}
                          </span>
                        )}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                        <Schedule />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col>
              <Card className="card-stats mb-4 mb-xl-0">
                <CardBody>
                  <Row>
                    <div className="col">
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0"
                      >
                        Executed By
                      </CardTitle>
                      <span className="h5 font-weight-bold mb-0">
                        {executionInfo?.userName}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                        <AccountCircle />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col>
              <Card className="card-stats mb-4 mb-xl-0">
                <CardBody>
                  <Row>
                    <div className="col">
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0"
                      >
                        Questions Asked
                      </CardTitle>
                      <span className="h5 font-weight-bold mb-0">
                        {questions.length}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                        <LiveHelp />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Input box section */}
      <Container className="mt--8" fluid>
        <Card
          style={{
            backgroundColor: "transparent",
            padding: "0",
            margin: "0",
            textAlign: "center",
          }}
        >
          <CardBody
            style={{ background: "transparent", padding: "0", margin: "0" }}
          >
            <Row>
              <Col className="col-12">
                <div className="mb-0 form-group">
                  <div className="input-group-alternative input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text text-warning">
                        <i className="fas fa-search"></i>
                      </span>
                    </div>
                    <input
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      placeholder="Ask your question from images.."
                      type="text"
                      className="form-control"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      {/* Ask question btn section */}
      <Container id="ask_question_btn_section" fluid>
        <Card
          style={{
            padding: "0",
            background: "transparent",
            border: "none",
            color: "#fff",
          }}
        >
          <CardBody>
            <Row>
              <Col className="text-center">
                <Button
                  onClick={() => askQuestionFromImages()}
                  className="btn-sm btn-warning"
                  disabled={isAskquestionLoading || !questionText}
                >
                  Ask Question from images
                </Button>
                {isAskquestionLoading && <Spinner size="sm" />}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      {/* Answers Section */}
      <Container id="asked_question_section" fluid>
        <Row className="mt-5">
          <Col>
            <h1>
              <DynamicFeed /> Asked Questions{" "}
              <small style={{ fontSize: "12px", letterSpacing: ".08em" }}>
                (Recent First)
              </small>
              &nbsp;
              {isLoading && (
                <>
                  <Spinner />
                </>
              )}
            </h1>
          </Col>
        </Row>
      </Container>

      {/* Single Asnwer container */}
      {questions.map((question, index) => (
        <SingleQuestionImage
          key={index}
          question={{
            question: question.question,
            messages: question.response.messages,
            cost: question.response.cost,
            id: question.id,
            executionId: question.executionId,
          }}
          defaultOpen={index === 0}
        />
      ))}
      {questions.length < 1 && (
        <Container fluid>No questions asked yet.</Container>
      )}

      {/* Images Section */}
      <Container id="asked_question_section" fluid>
        <Row className="mt-5">
          <Col>
            <h1>
              <ImageOutlined />
              &nbsp;Uploaded Images{" "}
            </h1>
          </Col>
        </Row>
      </Container>
      <Container className="mb-4" fluid>
        <Card>
          <CardHeader>
            <div className="sticky-section"></div>
            <Row className="align-items-center" id="txt_section">
              <Col>
                <div>
                  <small>
                    <b>Total:&nbsp;{images.length} &nbsp;Images</b>
                  </small>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Row>
              <Col className="col-12">
                {images && images.length > 0 && (
                  <ImageGallery
                    thumbnailPosition="left"
                    showBullets={false}
                    showFullscreenButton={false}
                    items={images}
                    showPlayButton={false}
                  />
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default ImageIntellExecutionPage;
